import { CacheType } from '@descomplica/caching-browser';
import {
  BaseHttpGateway,
  cache,
  CollaborationResult,
  RecognitionManager,
} from '@descomplica/microfrontend-core';
import { injectable } from 'tsyringe';

import type {
  AllEssayDto,
  ApplicationDto,
  ClassifierDto,
  EssayDto,
  EssayHighlightDto,
  EssayMediaDto,
  EssayThemeDto,
  IPegasusGateway,
  LiveStreamingDto,
  QuizDto,
  StudyPlanDto,
  UserDto,
} from '@/application/interfaces/pegasus.gateway';
import { EssayInput, EssayMediaInput } from '@/presentation/screens/essay/types';

import envs from '../config/envs';

@injectable()
export class PegasusGateway extends BaseHttpGateway implements IPegasusGateway {
  constructor(private readonly recognitionManager: RecognitionManager) {
    super();
  }

  async getIdentifiedUser(): Promise<CollaborationResult<UserDto>> {
    const { data } = await this.axios.get(`/identified-user`);
    return data;
  }

  async getApplication(): Promise<CollaborationResult<ApplicationDto>> {
    const url = `/application`;
    const { data } = await this.axios.get(url);
    return data;
  }

  @cache(`getLibraryByGuid`, {
    ttl: 60000,
  })
  async getLibraryByClassifierGuid(
    classifierGuid: string,
  ): Promise<CollaborationResult<ClassifierDto[]>> {
    const { data } = await this.axios.get(`/library/${classifierGuid}`);
    return data;
  }

  @cache(`getCurrentStudentClasses`, {
    ttl: 60000,
  })
  async getCurrentStudentClasses(classifierGuid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/current/student-classes/${classifierGuid}`);
    if (data?.shouldRedirect && data?.redirectTo) {
      window.location.href = data.redirectTo;
    }
    return data;
  }

  @cache(`getHighestClassificationLevels`, {
    ttl: 60000,
  })
  async getHighestClassificationLevels(): Promise<CollaborationResult<ClassifierDto[]>> {
    const { data } = await this.axios.get(`/highest-level-classifiers`);
    return data;
  }

  @cache(`getStudyPlan`, {
    ttl: 60000,
  })
  async getStudyPlan(guid: string): Promise<CollaborationResult<StudyPlanDto>> {
    const { data } = await this.axios.get(`/study-plan/${guid}`);
    return data;
  }

  @cache(`getLiveStreamingByGuid`, {
    ttl: 60000,
  })
  async getLiveStreamingByGuid(guid: string): Promise<CollaborationResult<LiveStreamingDto>> {
    const { data } = await this.axios.get(`/library/streaming/live/${guid}`);
    return data;
  }

  @cache(`getExercisesByClassifier`, {
    ttl: 60000,
  })
  async getExercisesByClassifier(guid: string): Promise<CollaborationResult<QuizDto[]>> {
    const { data } = await this.axios.get(`/exercises/classifier/${guid}`);
    return data;
  }

  async getEssay(guid: string): Promise<CollaborationResult<EssayDto>> {
    const { data } = await this.axios.get(`${this.getEndpointEssay()}/essays/${guid}`);
    return data;
  }

  async getAllEssays(): Promise<CollaborationResult<AllEssayDto>> {
    const { data } = await this.axios.get(`${this.getEndpointEssay()}/essays`);
    return data;
  }

  async getEssayHighlights(): Promise<CollaborationResult<EssayHighlightDto[]>> {
    const { data } = await this.axios.get(`${this.getEndpointEssay()}/highlights`);
    return data.data;
  }

  async getEssayTheme(guid: string): Promise<CollaborationResult<EssayThemeDto>> {
    const { data } = await this.axios.get(`/${guid}`);
    return data;
  }

  async getAllEssayThemes(): Promise<CollaborationResult<EssayThemeDto[]>> {
    const { data } = await this.axios.get(`${this.getEndpointEssay()}/topics`);
    return data.data;
  }

  async deleteEssayDraft(guid: string): Promise<CollaborationResult<void>> {
    await this.axios.delete(`/${guid}`);

    return;
  }

  async createEssay(input: EssayInput): Promise<CollaborationResult<EssayDto>> {
    const url = `${this.getEndpointEssay()}/essays`;
    const { data } = await this.axios.post(url, input);

    return data;
  }

  async createEssayMedia(input: EssayMediaInput): Promise<CollaborationResult<EssayMediaDto>> {
    const url = `${this.getEndpointAiMedia()}/image-to-text/extractions/upload`;

    const formData = new FormData();
    formData.append('file', input.file, input.file.name);

    const { data } = await this.axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return data.data;
  }

  async createEssayDraft(input: EssayInput): Promise<CollaborationResult<EssayDto>> {
    const url = `${this.getEndpointEssay()}/essays`;
    const { data } = await this.axios.post(url, input);

    return data;
  }

  async updateEssay(input: EssayInput): Promise<CollaborationResult<EssayDto>> {
    const url = `${this.getEndpointEssay()}/essays/${input.id}`;
    const { data } = await this.axios.patch(url, input);

    return data;
  }

  @cache(`getStudentClassByClassifierGuid`, {
    ttl: 60000,
  })
  async getStudentClassByClassifierGuid(classifierGuid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/current/student-classes/${classifierGuid} `);

    // TODO:Uncomment this when the redirect is fixed
    // if (data?.shouldRedirect && data?.redirectTo) {
    //   window.location.href = data.redirectTo;
    // }

    return data;
  }

  protected getEndpoint(): string {
    return `${envs.pegasusUrl}/application-management`;
  }

  protected getEndpointEssay(): string {
    return `${envs.pegasusUrl}/essays-management`;
  }

  protected getEndpointAiMedia(): string {
    return `${envs.pegasusUrl}/ai-media`;
  }

  protected getCacheType(): CacheType {
    return CacheType.LOCALSTORAGE;
  }

  protected getCacheNamespace(): string {
    return 'descomplica-io-frontend';
  }

  protected getBearerToken(): string {
    return this.recognitionManager.getAccessToken();
  }

  protected getHeaders(): object {
    const headers = {};
    headers['Cache-Control'] = 'no-cache';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = '0';
    return headers;
  }
}
